import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import * as actions from '../../features/schedule/redux/actions';
import CustomInputWithIcon from '../../common/ui/CustomInputWithIcon';
import CustomSelect from '../../common/formElements/CustomSelect';
import { builderSurfaceJsonContentHeader } from '../../common/utils/restApiHeaderUtils';
import axios from 'axios';
import { iconForField } from '../../common/custom/icon-library';

export class SyncGoogleCalendarEventsSchedule extends Component {
  static propTypes = {
    schedule: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  state = {
    googleCalendarCollections: [],
    allCollections: [],
    selectedCollectionFields: [],
  };

  async componentDidMount() {
    const { projectId, version, taskDetail } = this.props;
    const header = builderSurfaceJsonContentHeader(projectId);
    const response = await axios.get(
      `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/collections/${projectId}/project`,
      header,
    );
    const allCollections = response.data;
    const googleCalendarCollections = allCollections.filter(
      (collection) => collection.googleCalendarMapping.length,
    );
    let selectedCollectionFields = [];
    if (taskDetail.settings && taskDetail.settings.syncGoogleCalendarCollection) {
      const selectedCollection = allCollections.find(
        (c) => c.collectionName === taskDetail.settings.syncGoogleCalendarCollection,
      );
      if (selectedCollection && selectedCollection.fields) {
        selectedCollectionFields = selectedCollection.fields;
      }
    }
    this.setState({
      allCollections,
      googleCalendarCollections,
      selectedCollectionFields,
    });
  }

  handleSubmit = (values) => {
    this.props.handleSubmit(values);
  };

  scheduleSchema = () => {
    const { taskDetail } = this.props;
    if (taskDetail.settings) {
      const {
        syncGoogleCalendarCollection,
        userIdentifierField,
        eventIdField,
        fetchEventsTill,
        fetchOnlyFutureEvents,
      } = taskDetail.settings;
      return {
        syncGoogleCalendarCollection: syncGoogleCalendarCollection || '',
        userIdentifierField: userIdentifierField || '',
        eventIdField: eventIdField || '',
        fetchEventsTill: fetchEventsTill || '',
        fetchOnlyFutureEvents: fetchOnlyFutureEvents || false,
      };
    }
    return {
      syncGoogleCalendarCollection: '',
      userIdentifierField: '',
      eventIdField: '',
      fetchEventsTill: '',
      fetchOnlyFutureEvents: false,
    };
  };

  scheduleSchemaValidation = () => {
    return Yup.object().shape({
      syncGoogleCalendarCollection: Yup.string().required('Collection is required.'),
      userIdentifierField: Yup.string().required('User Identifier is required.'),
      eventIdField: Yup.string().required('Event ID is required.'),
      fetchEventsTill: Yup.string().required('Fetch range is required.'),
      fetchOnlyFutureEvents: Yup.boolean(),
    });
  };

  handleCancel = () => {
    const { projectId, version, history } = this.props;
    history.push(`/2.0/schedule/${version}/v/${projectId}/`);
  };

  handleCollectionChange = (name, value, setFieldValue) => {
    setFieldValue(name, value);
    const { allCollections } = this.state;
    const selectedCollection = allCollections.find(
      (collection) => collection.collectionName === value,
    );
    if (selectedCollection && selectedCollection.fields) {
      this.setState({
        selectedCollectionFields: selectedCollection.fields,
      });
    } else {
      this.setState({ selectedCollectionFields: [] });
    }
  };

  render() {
    const { googleCalendarCollections, selectedCollectionFields } = this.state;
    const updateSchedulePending = false;
    const googleCalendarCollectionOptions = googleCalendarCollections.map((collection) => ({
      label: collection.collectionTitle,
      value: collection.collectionName,
    }));
    const userIdentifierOptions = selectedCollectionFields.map((field) => ({
      label: field.fieldTitle.en,
      value: field.fieldName,
    }));
    const eventIdOptions = selectedCollectionFields.map((field) => ({
      label: field.fieldTitle.en,
      value: field.fieldName,
    }));

    return (
      <div className="schedule-sync-google-calendar-task-schedule">
        <Formik
          enableReinitialize={true}
          initialValues={this.scheduleSchema()}
          validationSchema={this.scheduleSchemaValidation()}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            this.handleSubmit(values);
            setSubmitting(false);
            resetForm();
          }}
        >
          {({ values, setFieldValue, setFieldTouched }) => (
            <Form className="form-horizontal">
              <div className="row">
                <div className="col-md-6">
                  <CustomInputWithIcon icon="collection" required={true} title="Collection">
                    <CustomSelect
                      name="syncGoogleCalendarCollection"
                      options={googleCalendarCollectionOptions}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      placeholder="Select Collection"
                      value={values.syncGoogleCalendarCollection}
                      onChangeValue={(name, value) =>
                        this.handleCollectionChange(name, value, setFieldValue)
                      }
                    />
                    <p className="text-danger">
                      <ErrorMessage name="syncGoogleCalendarCollection" />
                    </p>
                  </CustomInputWithIcon>
                </div>
                <div className="col-md-6">
                  <CustomInputWithIcon icon="user" required={true} title="User Identifier Field">
                    <CustomSelect
                      name="userIdentifierField"
                      options={userIdentifierOptions}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      placeholder="Select User Identifier Field"
                      value={values.userIdentifierField}
                    />
                    <p className="text-danger">
                      <ErrorMessage name="userIdentifierField" />
                    </p>
                  </CustomInputWithIcon>
                </div>
                <div className="col-md-6">
                  <CustomInputWithIcon icon="user" required={true} title="User Identifier Field">
                    <CustomSelect
                      name="eventIdField"
                      options={eventIdOptions}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      placeholder="Select Event ID Field"
                      value={values.eventIdField}
                    />
                    <p className="text-danger">
                      <ErrorMessage name="eventIdField" />
                    </p>
                  </CustomInputWithIcon>
                </div>
                <div className="col-md-6">
                  <CustomInputWithIcon icon="calendar" required={true} title="Fetch Events Till">
                    <CustomSelect
                      name="fetchEventsTill"
                      options={[
                        { label: 'Next 1 Week', value: '1w' },
                        { label: 'Next 2 Weeks', value: '2w' },
                        { label: 'Next 1 Month', value: '1m' },
                        { label: 'Next 3 Months', value: '3m' },
                        { label: 'Next 6 Months', value: '6m' },
                        { label: 'Next 1 Year', value: '1y' },
                        { label: 'All Future Events', value: 'all' },
                      ]}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      placeholder="Fetch Events Till"
                      value={values.fetchEventsTill}
                    />
                    <p className="text-danger">
                      <ErrorMessage name="fetchEventsTill" />
                    </p>
                  </CustomInputWithIcon>
                </div>
                <div className="col md-6">
                  <div className="input-group">
                    <div className="input-group-prepend" title="Fetch Only Future Events">
                      <span className="input-group-text">
                        <i className={`wb ${iconForField('boolean-toggle')}`}></i>
                      </span>
                    </div>
                    <div className="form-check">
                      <label className="form-check-label">
                        <Field
                          type="checkbox"
                          name="fetchOnlyFutureEvents"
                          className="form-check-input"
                          title="fetchOnlyFutureEvents"
                        />
                        Fetch Only Future Events
                        <span className="form-check-sign">
                          <span className="check"></span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-md"
                    onClick={this.handleCancel}
                  >
                    <i className="wb webconnect-times"></i> Cancel
                  </button>
                </div>
                <div className="col-md-6 text-right">
                  <button
                    type="submit"
                    className="btn btn-theme-submit btn-md"
                    disabled={updateSchedulePending}
                  >
                    Schedule
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    schedule: state.schedule,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SyncGoogleCalendarEventsSchedule);
